import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { getToken } from "@/lib/api";
import { enablePush, pushSupported } from "@/lib/push";
import { isImpersonating } from "@/lib/impersonate";

const Ctx = createContext({ items: [], unread: 0 });
const STREAM = `${process.env.REACT_APP_BACKEND_URL}/api/notifications/stream`;

/** In-app bell: initial list + live updates over Server-Sent Events. Fires a "task-updated" window event for open task pages. */
export function NotificationsProvider({ children }) {
  const [state, setState] = useState({ items: [], unread: 0 });
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/notifications").then((r) => setState(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    if (pushSupported() && Notification.permission === "granted" && !isImpersonating()) enablePush().catch(() => {});
    const es = new EventSource(`${STREAM}?token=${encodeURIComponent(getToken() || "")}`);
    es.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === "feed_changed") {
        if (msg.kind === "notifications") setState((s) => ({ items: s.items.filter((x) => !msg.ids.includes(x.id)), unread: s.unread }));
        window.dispatchEvent(new CustomEvent("task-updated", { detail: msg.task_id }));
        return;
      }
      if (msg.type !== "notification") return;
      const n = msg.notification;
      setState((s) => ({ items: [n, ...s.items].slice(0, 50), unread: s.unread + 1 }));
      window.dispatchEvent(new CustomEvent("task-updated", { detail: n.task_id }));
      toast(n.title, { description: n.text, action: { label: "Open", onClick: () => navigate(n.url) } });
    };
    return () => es.close();
  }, [load, navigate]);

  const markRead = useCallback(async (n) => {
    if (!n.read) {
      setState((s) => ({ items: s.items.map((x) => (x.id === n.id ? { ...x, read: true } : x)), unread: Math.max(0, s.unread - 1) }));
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
  }, []);
  const markAll = useCallback(async () => {
    setState((s) => ({ items: s.items.map((x) => ({ ...x, read: true })), unread: 0 }));
    await api.post("/notifications/read-all").catch(() => {});
  }, []);

  return <Ctx.Provider value={{ ...state, markRead, markAll, reload: load }}>{children}</Ctx.Provider>;
}

export const useNotifications = () => useContext(Ctx);
